import type { PublicOrderNormalizeKind } from "./orderSummaryKinds";

const API_URL = process.env.REACT_APP_API_URL;

export type PaymentMethodsCountResponse = {
  count: number;
  has_payment_methods: boolean;
};

function parsePaymentMethodsCountPayload(raw: unknown): PaymentMethodsCountResponse {
  const payload =
    raw && typeof raw === "object" && "has_payment_methods" in (raw as Record<string, unknown>)
      ? (raw as Record<string, unknown>)
      : raw && typeof raw === "object" && "data" in (raw as Record<string, unknown>)
        ? ((raw as { data?: Record<string, unknown> }).data ?? {})
        : {};
  return {
    count: Number(payload.count ?? 0),
    has_payment_methods: Boolean(payload.has_payment_methods),
  };
}

/** GET /public/payment-methods/count/organizations/{organization_id}/memberships/{membership_id} */
export async function fetchPaymentMethodsCountByOrganizationAndMembership(
  organizationId: string,
  membershipId: string,
  _token?: string
): Promise<PaymentMethodsCountResponse> {
  const orgId = String(organizationId || "").trim();
  const memId = String(membershipId || "").trim();
  if (!orgId || !memId) {
    return { count: 0, has_payment_methods: false };
  }

  const response = await fetch(
    `${API_URL}/public/payment-methods/count/organizations/${encodeURIComponent(orgId)}/memberships/${encodeURIComponent(memId)}`,
    { headers: { Accept: "application/json" } }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg =
      (raw as { error?: string })?.error ||
      (raw as { message?: string })?.message ||
      "Failed to load payment methods";
    throw new Error(typeof msg === "string" ? msg : "Failed to load payment methods");
  }
  return parsePaymentMethodsCountPayload(raw);
}

async function parseFinalizeError(response: Response): Promise<string> {
  const raw = await response.json().catch(() => ({}));
  const msg =
    (raw as { error?: string })?.error ||
    (raw as { message?: string })?.message ||
    "Failed to finalize order";
  return typeof msg === "string" ? msg : "Failed to finalize order";
}

/** PUT /public/orders/{order_id}/finalize */
export async function finalizePublicSalesOrder(orderId: string): Promise<void> {
  const id = String(orderId || "").trim();
  if (!id) throw new Error("Missing order id");

  const response = await fetch(`${API_URL}/public/orders/${encodeURIComponent(id)}/finalize`, {
    method: "PUT",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!response.ok) {
    throw new Error(await parseFinalizeError(response));
  }
}

/** POST /public/installation_orders/{order_id}/finalize */
export async function finalizePublicInstallationOrder(orderId: string): Promise<void> {
  const id = String(orderId || "").trim();
  if (!id) throw new Error("Missing order id");

  const response = await fetch(
    `${API_URL}/public/installation_orders/${encodeURIComponent(id)}/finalize`,
    { method: "POST", headers: { Accept: "application/json" } }
  );
  if (!response.ok) {
    throw new Error(await parseFinalizeError(response));
  }
}

/** POST /public/removal_orders/{order_id}/finalize */
export async function finalizePublicRemovalOrder(orderId: string): Promise<void> {
  const id = String(orderId || "").trim();
  if (!id) throw new Error("Missing order id");

  const response = await fetch(
    `${API_URL}/public/removal_orders/${encodeURIComponent(id)}/finalize`,
    { method: "POST", headers: { Accept: "application/json" } }
  );
  if (!response.ok) {
    throw new Error(await parseFinalizeError(response));
  }
}

export async function finalizePublicOrderForSummary(
  orderKind: PublicOrderNormalizeKind,
  orderId: string
): Promise<void> {
  if (orderKind === "installation") {
    await finalizePublicInstallationOrder(orderId);
    return;
  }
  if (orderKind === "removal") {
    await finalizePublicRemovalOrder(orderId);
    return;
  }
  await finalizePublicSalesOrder(orderId);
}
